/**
 * The one place a request leaves this application.
 *
 * Every API module is built on this, which is what makes three things true
 * everywhere rather than in most places:
 *
 *   - the access token is attached, and only ever in the `Authorization`
 *     header;
 *   - a failure arrives as a typed error carrying the API's own message,
 *     rather than as `undefined` flowing on into a component;
 *   - a request can be abandoned, so a view that closes does not leave work
 *     running behind it.
 *
 * `fetch` rather than a client library: what is needed here is a handful of
 * verbs, a header, and cancellation, all of which the platform provides.
 */

import { chosenDevAccount } from '@/auth/devAccount'
import { config } from '@/app/config'

/** Supplies the current access token. Set once during boot. */
type TokenSource = () => string | null

let readToken: TokenSource = () => null

/**
 * Tell the client where tokens come from.
 *
 * Injected rather than imported, so this module has no dependency on the
 * store, and a test can drive it without one.
 */
export function useTokenSource(source: TokenSource): void {
  readToken = source
}

/** Called when the API rejects a token, so the session can be re-established. */
type ExpiryHandler = () => void

let onExpired: ExpiryHandler = () => {}

export function onTokenExpired(handler: ExpiryHandler): void {
  onExpired = handler
}

/** A request that did not succeed. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** The API's own message, safe to show a user. */
    message: string,
    /** Machine-readable code, when the API supplied one. */
    readonly code: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** The caller is not signed in, or their token was refused. */
  get isUnauthorised(): boolean {
    return this.status === 401
  }

  /** Signed in, but not permitted to do this. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** No such thing, or nothing the caller is allowed to know exists. */
  get isNotFound(): boolean {
    return this.status === 404
  }

  /** The request was rejected as invalid rather than refused. */
  get isInvalid(): boolean {
    return this.status === 400 || this.status === 422
  }
}

/** The network did not carry the request at all. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('The server could not be reached.', { cause })
    this.name = 'NetworkError'
  }
}

/** The shape the API uses to report a failure. */
interface ErrorBody {
  error?: { code?: string; message?: string }
  detail?: string | { msg?: string }[]
}

async function describe(response: Response): Promise<ApiError> {
  let message = `The request failed (${response.status}).`
  let code: string | null = null

  try {
    const body = (await response.json()) as ErrorBody
    if (body.error?.message) {
      message = body.error.message
      code = body.error.code ?? null
    } else if (typeof body.detail === 'string') {
      message = body.detail
    } else if (Array.isArray(body.detail)) {
      // Validation failures arrive as a list of problems; the first one is
      // what a person needs to see.
      const first = body.detail[0]
      if (first?.msg) message = first.msg
    }
  } catch {
    // A failure with no JSON body is ordinary: a gateway timing out, or a
    // proxy answering before the API did. The status is all there is.
  }

  return new ApiError(response.status, message, code)
}

export interface RequestOptions {
  /** Query parameters. Absent and empty values are dropped, not sent blank. */
  readonly query?: Readonly<Record<string, string | number | boolean | null | undefined>>
  /** Abandons the request when the caller no longer needs the answer. */
  readonly signal?: AbortSignal
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${config.api.baseUrl}/api/v1${path}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue
    params.append(key, String(value))
  }

  const search = params.toString()
  return search ? `${url}?${search}` : url
}

function authHeaders(): Record<string, string> {
  const token = readToken()
  // Development only, with no portal: which account to act as. A name, not a token.
  const account = chosenDevAccount()
  if (account) return { 'X-Dev-Account': account }
  // Only ever this header. A token in the query string would land in proxy
  // logs and browser history; a token in a cookie would make every endpoint
  // reachable by cross-site request forgery.
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** An abort is the caller's own doing, not a failure to report. */
function rethrow(cause: unknown): never {
  if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
  throw new NetworkError(cause)
}

async function send<T>(
  method: string,
  path: string,
  body: unknown,
  options: RequestOptions,
): Promise<T> {
  const isForm = body instanceof FormData
  const headers: Record<string, string> = { Accept: 'application/json', ...authHeaders() }
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json'

  const init: RequestInit = { method, headers }
  if (body !== undefined) init.body = isForm ? body : JSON.stringify(body)
  if (options.signal) init.signal = options.signal

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), init)
  } catch (cause) {
    rethrow(cause)
  }

  if (response.status === 401) {
    onExpired()
    throw await describe(response)
  }

  if (!response.ok) throw await describe(response)

  // 204, and any other answer with nothing in it.
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T
  }

  return (await response.json()) as T
}

/** A file the API produced, with the name it chose for it. */
export interface DownloadedFile {
  readonly blob: Blob
  readonly filename: string
}

export const http = {
  get: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
    send<T>('GET', path, undefined, options),

  post: <T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> =>
    send<T>('POST', path, body, options),

  put: <T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> =>
    send<T>('PUT', path, body, options),

  patch: <T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> =>
    send<T>('PATCH', path, body, options),

  delete: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
    send<T>('DELETE', path, undefined, options),

  /**
   * Fetch a file rather than JSON: exports come back as bytes under a name the
   * API chose, so both are returned.
   */
  async download(
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<DownloadedFile> {
    const headers: Record<string, string> = { ...authHeaders() }
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    const init: RequestInit = { method: body === undefined ? 'GET' : 'POST', headers }
    if (body !== undefined) init.body = JSON.stringify(body)
    if (options.signal) init.signal = options.signal

    let response: Response
    try {
      response = await fetch(buildUrl(path, options.query), init)
    } catch (cause) {
      rethrow(cause)
    }

    if (response.status === 401) onExpired()
    if (!response.ok) throw await describe(response)

    return {
      blob: await response.blob(),
      filename: filenameFrom(response.headers.get('Content-Disposition')),
    }
  },
}

const ENCODED_FILENAME = /filename\*=UTF-8''([^;]+)/i
const PLAIN_FILENAME = /filename="?([^";]+)"?/i

/**
 * The filename the API asked for.
 *
 * Prefers the encoded form, which is what a report titled "Résultats" arrives
 * under. Falls back to the plain one, and finally to a name of our own, since
 * a download with no name is worse than a generic one.
 */
export function filenameFrom(disposition: string | null): string {
  if (!disposition) return 'export'

  const encoded = ENCODED_FILENAME.exec(disposition)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1])
    } catch {
      // A malformed encoding is not worth failing a download over.
    }
  }

  return PLAIN_FILENAME.exec(disposition)?.[1] ?? 'export'
}
